import { toggle } from "@otto-ec/global-resources/toggle";
import { FLOATING_BUTTON_LOCAL_STORAGE_KEY } from "../config";
import { AuthenticationTokenService } from "./AuthenticationTokenService";

const DEFAULT_PAGE_CLUSTER_WHITELIST = ["Storefront", "Einstiegsseite", "Produktliste", "Suchergebnisseite", "Artikeldetailseite", "Artikelkombiseite"];
const DEFAULT_PATH_WHITELIST: string[] = [];
const DEFAULT_PATH_BLACKLIST: string[] = ["/themen/4gewinnt","/themen/4gewinnt/"];

interface ShopIntegrationConfig {
    whitelist?: string[];
    pathWhitelist?: string[];
    pathBlacklist?: string[];
}

export class ShopIntegrationService {
    private readonly config: ShopIntegrationConfig;

    constructor(config: ShopIntegrationConfig = {}) {
        this.config = {
            whitelist: config.whitelist || DEFAULT_PAGE_CLUSTER_WHITELIST,
            pathWhitelist: config.pathWhitelist || DEFAULT_PATH_WHITELIST,
            pathBlacklist: config.pathBlacklist || DEFAULT_PATH_BLACKLIST,
        };
    }

    /**
 *
 */
    async canShowChatbotEntrypoint(): Promise<boolean> {
        const emergencyToggle = toggle.get("nucleus_chatbot_emergency_disable_entrypoint", false);

        /*                                                                */
        if (emergencyToggle) {
            return false;
        }

        const isInWhitelistedCluster = this.isInWhitelistedPageCluster();

        /*                                                                   */
        if (isInWhitelistedCluster) {
            return !this.isPathBlacklisted();
        }

        /*                                                                       */
        return this.isPathWhitelisted();
    }

    /**
 *
 */
    async isLoggedInUserInApp(): Promise<boolean> {
        const [isInApp, userIsLoggedIn] = await Promise.all([this.isInApp(), this.userIsLoggedIn()]);

        /*                                                */
        if (!isInApp) {
            return false;
        }

        /*                                    */
        return userIsLoggedIn;
    }

    isInWhitelistedPageCluster(): boolean {
        const pageCluster = this.getPageCluster();
        return !!pageCluster && this.config.whitelist!.includes(pageCluster);
    }

    async shouldShowButton() {
        return (await this.isLoggedInUserInApp() || AuthenticationTokenService.hasToken());
    }

    buttonCanBeBanner() {
        let wasClosed = false;
        try {
            wasClosed = !!localStorage?.getItem(FLOATING_BUTTON_LOCAL_STORAGE_KEY);
        } catch (error) { }
        return this.getPageCluster() === "Storefront" && !wasClosed;
    }

    buttonCanBeExpandable(): boolean {
        const pageCluster = this.getPageCluster();
        return !!(pageCluster && (pageCluster !== "Produktliste" && pageCluster !== "Suchergebnisseite"));
    }

    /**
 *
 */
    getPageCluster(): string | null {
        return window.o_util.misc.getPagecluster();
    }

    /**
 *
 */
    getCurrentPath(): string {
        return window.location.pathname;
    }

    /**
 *
 *
 */
    isPathWhitelisted(): boolean {
        const currentPath = this.getCurrentPath();

        /*                                                                             */
        if (this.config.pathWhitelist!.length === 0) {
            return false;
        }

        /*                                                    */
        return this.config.pathWhitelist!.some(whitelistedPath =>
            this.pathMatches(currentPath, whitelistedPath)
        );
    }

    /**
 *
 *
 */
    isPathBlacklisted(): boolean {
        const currentPath = this.getCurrentPath();

        /*                                                    */
        return this.config.pathBlacklist!.some(blacklistedPath =>
            this.pathMatches(currentPath, blacklistedPath)
        );
    }

    /**
 *
 *
 *
 */
    private pathMatches(path: string, pattern: string): boolean {
        /*          */
        if (path === pattern) {
            return true;
        }

        /*                        */
        if (pattern.includes('*')) {
            const regexPattern = pattern
                .replace(/[.*+?^${}()|[\]\\]/g, '\\$&') /*                         */
                .replace(/\\\*/g, '.*'); /*                 */

            const regex = new RegExp(`^${regexPattern}$`);
            return regex.test(path);
        }

        /*                                       */
        if (pattern.endsWith('/')) {
            return path.startsWith(pattern);
        }

        return false;
    }

    /**
 *
 */
    userIsLoggedIn(): Promise<boolean> {
        return this.retryUntilResolved(() => window.o_user?.loginState?.presenter?.isLoggedIn(), false);
    }

    /**
 *
 */
    isInApp(): Promise<boolean> {
        return this.retryUntilResolved(() => window.o_apps?.runningInApp(), false);
    }

    /**
 *
 *
 *
 *
 *
 *
 */
    private retryUntilResolved<T, D>(condition: () => T | undefined, defaultValue: D, retries: number = 20, delay: number = 100): Promise<T | D> {
        return new Promise((resolve) => {
            const attempt = () => {
                const result = condition();
                if (result !== undefined) {
                    resolve(result);
                } else if (retries > 0) {
                    retries--;
                    setTimeout(attempt, delay);
                } else {
                    resolve(defaultValue); /*      */
                }
            };
            attempt();
        });
    }


}

export default new ShopIntegrationService();
